import Service from "@ember/service";
import { tracked } from "@glimmer/tracking";
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";

export enum SemeiaFeature {
  DmpConnectIsDevelopmentMode = "DmpConnectIsDevelopmentMode"
}

type SemeiaFeatureActivations = Record<SemeiaFeature, boolean>;

/**
 * A service for handling feature flipping.
 * The status is stored in the local storage.
 */
export default class SemeiaFeaturesService extends Service {
  static readonly featuresKey = "features:activations";

  private static defaultFeatures = {
    DmpConnectIsDevelopmentMode: false
  };

  private static get storedFeatures(): SemeiaFeatureActivations {
    const serializedFeatures = getItemFromLocalStorage(
      SemeiaFeaturesService.featuresKey
    );
    if (serializedFeatures) {
      return JSON.parse(serializedFeatures);
    }

    return this.defaultFeatures;
  }

  @tracked private features: SemeiaFeatureActivations =
    SemeiaFeaturesService.storedFeatures;

  private static storeFeatures(features: SemeiaFeatureActivations): void {
    setItemInLocalStorage(
      SemeiaFeaturesService.featuresKey,
      JSON.stringify(features)
    );
  }

  disable(feature: SemeiaFeature): void {
    const features = SemeiaFeaturesService.storedFeatures;
    features[feature] = false;
    SemeiaFeaturesService.storeFeatures(features);
    this.features = features;
  }

  enable(feature: SemeiaFeature): void {
    const features = SemeiaFeaturesService.storedFeatures;
    features[feature] = true;
    SemeiaFeaturesService.storeFeatures(features);
    this.features = features;
  }

  isEnabled(feature: SemeiaFeature): boolean {
    return this.features[feature];
  }

  reset(): void {
    SemeiaFeaturesService.storeFeatures(SemeiaFeaturesService.defaultFeatures);
    this.features = SemeiaFeaturesService.defaultFeatures;
  }
}

declare module "@ember/service" {
  interface Registry {
    "semeia-features": SemeiaFeaturesService;
  }
}
